//--------------- IMPORTAÇÕES ---------------//
import { useState, useEffect, useRef } from "react";
import { Flame, Mail, Phone, Tag as TagIcon, ShoppingCart, Globe, MessageCircle, FileText, Zap, CreditCard, Loader2 } from "lucide-react";
import { loadTagsUserService } from "../services";


//--------------- HELPER: TEMPO RELATIVO PT-BR ---------------//
function tempoRelativo(dataIso) {
    if (!dataIso) return "sem registro";
    const data = new Date(dataIso);
    if (isNaN(data.getTime())) return "sem registro";
    const diffMs = Date.now() - data.getTime();
    if (diffMs < 0) return "agora";
    const minutos = Math.floor(diffMs / 60000);
    if (minutos < 1) return "agora";
    if (minutos < 60) return `há ${minutos} ${minutos === 1 ? "minuto" : "minutos"}`;
    const horas = Math.floor(minutos / 60);
    if (horas < 24) return `há ${horas} ${horas === 1 ? "hora" : "horas"}`;
    const dias = Math.floor(horas / 24);
    if (dias < 30) return `há ${dias} ${dias === 1 ? "dia" : "dias"}`;
    const meses = Math.floor(dias / 30);
    return `há ${meses} ${meses === 1 ? "mês" : "meses"}`;
}


//--------------- MAPA DE SINAIS (chave -> label + ícone) ---------------//
const SINAIS = {
    carrinho_abandonado: { label: "Carrinho abandonado", icon: ShoppingCart },
    boleto: { label: "Boleto gerado", icon: FileText },
    pix: { label: "PIX gerado", icon: CreditCard },
    tentativa: { label: "Tentativa de compra", icon: Zap },
    whatsapp: { label: "Respondeu WhatsApp", icon: MessageCircle },
    tracking: { label: "Visitou o site", icon: Globe },
};


//--------------- BADGE DE SCORE ---------------//
function ScoreBadge({ score }) {
    const valor = typeof score === "number" ? score : 0;
    let cls;
    if (valor >= 70) {
        // tom quente forte com glow (danger = rose/vermelho do brand system)
        cls = "bg-danger-500/15 text-danger-400 ring-1 ring-danger-500/30 shadow-glow-danger";
    } else if (valor >= 40) {
        cls = "bg-warning-500/[0.12] text-warning-400 ring-1 ring-warning-500/25";
    } else {
        cls = "bg-white/[0.04] text-ink-3 ring-1 ring-white/[0.06]";
    }
    return (
        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold tabular-nums ${cls}`}>
            <Flame className="h-3.5 w-3.5" strokeWidth={2.4} />
            {valor}
        </span>
    );
}


//--------------- CARD INDIVIDUAL ---------------//
function CardLeadQuente({ lead }) {

    const [tags, setTags] = useState(null);
    const [carregandoTags, setCarregandoTags] = useState(false);
    const [mostrarTags, setMostrarTags] = useState(false);

    const montadoRef = useRef(true);
    useEffect(() => {
        montadoRef.current = true;
        return () => { montadoRef.current = false; };
    }, []);

    const sinaisAtivos = Object.entries(SINAIS).filter(
        ([chave]) => lead.sinais && lead.sinais[chave]
    );

    async function handleVerTags() {
        if (mostrarTags) { setMostrarTags(false); return; }
        setMostrarTags(true);
        if (tags === null) {
            setCarregandoTags(true);
            const response = await loadTagsUserService(lead.id);
            if (!montadoRef.current) return;
            if (response) setTags(response.data || []);
            else setTags([]);
            setCarregandoTags(false);
        }
    }

    const comprouSim = lead.comprou === true || lead.comprou === "Sim";

    return (
        <div className="surface-card surface-card-hover p-5 flex flex-col">

            {/* CABEÇALHO: NOME + SCORE */}
            <div className="flex justify-between items-start gap-3 mb-3">
                <h3 className="heading-3 text-brand-300 truncate" title={lead.nome}>
                    {lead.nome || "Sem nome"}
                </h3>
                <ScoreBadge score={lead.score} />
            </div>

            {/* CONTATO */}
            <div className="space-y-1.5 mb-3">
                <p className="flex items-center gap-2 text-sm text-ink-2 truncate" title={lead.email}>
                    <Mail className="h-4 w-4 text-ink-4 flex-shrink-0" /> <span className="truncate">{lead.email || "—"}</span>
                </p>
                <p className="flex items-center gap-2 text-sm text-ink-2">
                    <Phone className="h-4 w-4 text-ink-4 flex-shrink-0" /> {lead.telefone || "—"}
                </p>
            </div>

            {/* CHIPS DE SINAIS ATIVOS */}
            {sinaisAtivos.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-3">
                    {sinaisAtivos.map(([chave, { label, icon: Icon }]) => (
                        <span key={chave} className="chip-brand !text-[11px] !py-0.5">
                            <Icon className="h-3 w-3" /> {label}
                        </span>
                    ))}
                </div>
            )}

            {/* META: ÚLTIMA INTERAÇÃO + COMPROU */}
            <div className="flex items-center justify-between text-xs mt-auto pt-2">
                <span className="text-ink-4">
                    Última interação: <span className="text-ink-3">{tempoRelativo(lead.ultima_interacao)}</span>
                </span>
                <span className="text-ink-4">
                    Comprou: <span className={`font-semibold ${comprouSim ? "text-positive-400" : "text-danger-400"}`}>
                        {comprouSim ? "Sim" : "Não"}
                    </span>
                </span>
            </div>

            {/* AÇÃO: VER TAGS (inline, sem acoplamento ao LeadsContext) */}
            <div className="mt-3 pt-3 border-t border-white/[0.06]">
                <button
                    onClick={handleVerTags}
                    className="inline-flex items-center gap-1.5 text-xs text-ink-3 hover:text-brand-300 transition-colors"
                >
                    <TagIcon className="h-3.5 w-3.5" />
                    {mostrarTags ? "Ocultar tags" : "Ver tags"}
                </button>

                {mostrarTags && (
                    <div className="mt-2">
                        {carregandoTags ? (
                            <span className="flex items-center gap-1.5 text-xs text-ink-4">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Carregando...
                            </span>
                        ) : tags && tags.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5">
                                {tags.map((tag) => (
                                    <span key={tag.id || tag.nome} className="chip-neutral !text-[11px] !py-0.5">
                                        {tag.nome}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <span className="text-xs text-ink-4">Nenhuma tag.</span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}


//--------------- GRID DE CARDS ---------------//
export default function ContainerLeadsQuentes({ leads }) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {leads.map((lead) => (
                <CardLeadQuente key={lead.id} lead={lead} />
            ))}
        </div>
    );
}
